import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import ChartProvider from '../../../charts/echarts/ChartProvider'
import ChartColorProvider from '../../../charts/echarts/ColorProvider'
import Tooltip from '../../../charts/echarts/Tooltip'
import ComponentFunnel from '../../../charts/echarts/ComponentFunnel'
import { useFormattingContext } from '../../../organisms/FormattingProvider/FormattingContext'
import { buildLegendConfig, buildLabelConfig, EMPTY_OBJ } from './chartLayoutUtils'

function FunnelChartLayout ({ config, data, styleOverrides }) {
  const {
    height = 400,
    colorTheme = 'primary',
    nameAccessor,
    valueAccessor,
    sort = 'descending',
    orient = 'vertical',
    gap = 0,
    funnelAlign = 'center',
    tooltip = {},
    display = {},
    overrides = null,
    seriesOverrides = null
  } = config

  const variantProps = styleOverrides?.props ?? {}
  const effectiveGap = variantProps.gap ?? gap
  const effectiveFunnelAlign = variantProps.funnelAlign ?? funnelAlign

  const { label: displayLabel = EMPTY_OBJ, legend: displayLegend = EMPTY_OBJ, grid: displayGrid = EMPTY_OBJ } = display

  const legendConfig = buildLegendConfig(displayLegend)

  const { formatter: fmtFn } = useFormattingContext()
  const labelConfig = useMemo(() => {
    const base = buildLabelConfig(displayLabel)
    if (displayLabel.format) {
      return { ...base, formatter: (params) => fmtFn(params.value, displayLabel.format) }
    }
    return base
  }, [displayLabel, fmtFn])

  const chartData = useMemo(() => {
    if (!Array.isArray(data) || !nameAccessor || !valueAccessor) return []
    return data.map(row => ({
      name: get(row, nameAccessor),
      value: Number(get(row, valueAccessor)) || 0
    }))
  }, [data, nameAccessor, valueAccessor])

  const funnelMax = useMemo(() => {
    if (!chartData.length) return 100
    return chartData.reduce((max, d) => Math.max(max, d.value), 0) || 100
  }, [chartData])

  return (
    <ChartColorProvider theme={colorTheme}>
      <ChartProvider height={height} loadingTemplate='pie' {...(legendConfig && { legend: legendConfig })} {...(overrides || {})}>
        <Tooltip
          trigger={tooltip.trigger || 'item'}
          {...(tooltip.valueFormat && { valueFormat: tooltip.valueFormat })}
        />
        <ComponentFunnel
          seriesId='funnel'
          data={chartData}
          min={0}
          max={funnelMax}
          sort={sort}
          orient={orient}
          gap={effectiveGap}
          funnelAlign={effectiveFunnelAlign}
          left={displayGrid.left ?? '10%'}
          top={displayGrid.top ?? '10%'}
          bottom={displayGrid.bottom ?? '10%'}
          label={labelConfig}
          {...(seriesOverrides || {})}
        />
      </ChartProvider>
    </ChartColorProvider>
  )
}

FunnelChartLayout.propTypes = {
  config: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  styleOverrides: PropTypes.object
}

export default FunnelChartLayout
